import React, { useEffect, useMemo, useState } from "react";
import OrderPeriodPicker from "../../components/OrderPeriodPicker";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  listAccountingOrders,
  getAccountingSuppliersOptions,
} from "../../api/accountingApi";
import Profilo from "../Profilo";
import OrderDetailModal from "../../components/orders/OrderDetailModal";
import "./AdminAccountingOrdini.css";

const CONTAB_OPTIONS = [
  { value: "all", label: "Tutti" },
  { value: "non_fatturabile", label: "Non fatturabile" },
  { value: "da_fatturare", label: "Da fatturare" },
  { value: "fatturato", label: "Fatturato" },
];

const GROUP_MODES = [
  { value: "none", label: "Nessuno" },
  { value: "fornitore", label: "Fornitore" },
  { value: "citta", label: "Città" },
  { value: "fattura", label: "Fattura" },
];

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

function fmtEur(cents) {
  if (cents == null) return "—";
  const n = Number(cents || 0);
  return (n / 100).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  });
}

function renderContabStatus(st) {
  let label = "";
  let cls = "acc-ord-status-chip";

  switch (st) {
    case "non_fatturabile":
      label = "Non fatturabile";
      cls += " acc-status--nonfatt";
      break;
    case "da_fatturare":
      label = "Da fatturare";
      cls += " acc-status--dafatt";
      break;
    case "fatturato":
      label = "Fatturato";
      cls += " acc-status--fatt";
      break;
    default:
      label = st || "-";
  }

  return <span className={cls}>{label}</span>;
}

function computeTotals(list) {
  const totals = {
    articoli_cents: list.reduce((s, x) => s + (x.articoli_cents || 0), 0),
    promo_cents: list.reduce((s, x) => s + (x.promo_cents || 0), 0),
    articoli_netti_cents: list.reduce(
      (s, x) => s + (x.articoli_netti_cents || 0),
      0,
    ),
    ns_articoli_cents: list.reduce((s, x) => s + (x.ns_articoli_cents || 0), 0),
    rosa_cents: list.reduce((s, x) => s + (x.rosa_cents || 0), 0),
    ns_rosa_cents: list.reduce((s, x) => s + (x.ns_rosa_cents || 0), 0),
    flat_fee_cents: list.reduce((s, x) => s + (x.flat_fee_cents || 0), 0),
    ns_total_cents: list.reduce((s, x) => s + (x.ns_total_cents || 0), 0),
  };
  for (const field of ["articoli_cents", "promo_cents", "articoli_netti_cents", "rosa_cents"]) {
    if (list.some(row => row[field] == null)) totals[field] = null;
  }
  return totals;
}

/* =========================
   Date helpers
========================= */

function isoToDisplay(iso) {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export default function AdminAccountingOrdini() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);

  const [filters, setFilters] = useState({
    from: "",
    to: "",
    fornitore_id: "",
    contab_status: "da_fatturare",
  });

  const [groupMode, setGroupMode] = useState("none");

  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({
    articoli_cents: 0,
    promo_cents: 0,
    articoli_netti_cents: 0,
    ns_articoli_cents: 0,
    rosa_cents: 0,
    ns_rosa_cents: 0,
    flat_fee_cents: 0,
    ns_total_cents: 0,
  });
  const [totalPages, setTotalPages] = useState(1);

  const [suppliers, setSuppliers] = useState([]);

  const [detailId, setDetailId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [openClientId, setOpenClientId] = useState(null);


  const hasAppliedDateFilters = Boolean(filters.from || filters.to);

  function openOrderDetail(id) {
    setDetailId(id);
    setDetailOpen(true);
  }

  function closeOrderDetail() {
    setDetailOpen(false);
    setDetailId(null);
  }

  function openClientDetail(clienteId) {
    if (!clienteId) return;
    setOpenClientId(clienteId);
  }

  function closeClientDetail() {
    setOpenClientId(null);
  }

  function goSupplier(supplierId) {
    navigate(
      `/admin/gestioneservizifornitore?supplier=${encodeURIComponent(
        String(supplierId),
      )}&tab=0&q=`,
    );
  }

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoadingSuppliers(true);
        const res = await getAccountingSuppliersOptions();
        if (cancelled) return;
        setSuppliers(Array.isArray(res) ? res : []);
      } catch (err) {
        console.error("[AdminAccountingOrdini] suppliers error:", err);
        toast.error("Errore caricamento fornitori");
      } finally {
        if (!cancelled) setLoadingSuppliers(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const res = await listAccountingOrders({
          page,
          pageSize,
          filters,
        });

        if (cancelled) return;

        setRows(Array.isArray(res.data) ? res.data : []);
        setTotals(
          res.totals || {
            articoli_cents: 0,
            promo_cents: 0,
            articoli_netti_cents: 0,
            ns_articoli_cents: 0,
            rosa_cents: 0,
            ns_rosa_cents: 0,
            flat_fee_cents: 0,
            ns_total_cents: 0,
          },
        );
        setTotalPages(res.totalPages || 1);
      } catch (err) {
        if (!cancelled) {
          console.error("[AdminAccountingOrdini] load error:", err);
          toast.error(err?.message || "Errore caricamento ordini");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, filters]);

  const sortedRows = useMemo(() => {
    if (!rows?.length) return [];

    const { key, direction } = sortConfig;
    if (!key) return [...rows];

    const dir = direction === "asc" ? 1 : -1;
    const out = [...rows];

    out.sort((a, b) => {
      let va = a[key];
      let vb = b[key];

      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;

      if (
        key === "created_at" ||
        key === "created_date" ||
        key.endsWith("_at") ||
        key.endsWith("_date")
      ) {
        va = new Date(va).getTime() || 0;
        vb = new Date(vb).getTime() || 0;
        if (va === vb) return 0;
        return va > vb ? dir : -dir;
      }

      if (
        typeof va === "number" ||
        typeof vb === "number" ||
        key === "id" ||
        key.endsWith("_cents")
      ) {
        va = Number(va) || 0;
        vb = Number(vb) || 0;
        if (va === vb) return 0;
        return va > vb ? dir : -dir;
      }

      const sa = String(va);
      const sb = String(vb);
      return sa.localeCompare(sb, "it-IT", { sensitivity: "base" }) * dir;
    });

    return out;
  }, [rows, sortConfig]);

  const groupedData = useMemo(() => {
    if (!sortedRows.length) return [];

    if (groupMode === "none") {
      return [
        {
          key: "ALL",
          label: null,
          rows: sortedRows,
          totals: computeTotals(sortedRows),
        },
      ];
    }

    const map = new Map();

    for (const r of sortedRows) {
      let key = "ALL";
      let label = null;

      if (groupMode === "fornitore") {
        key = String(r.fornitore_id || r.fornitore || "sconosciuto");
        label = r.fornitore || "Fornitore sconosciuto";
      } else if (groupMode === "citta") {
        const city = r.citta || "Senza città";
        const prov = r.provincia || "";
        key = `${city}|${prov}`;
        label = prov ? `${city} (${prov})` : city;
      } else if (groupMode === "fattura") {
        key = String(r.invoice_number || "senza_fattura");
        label = r.invoice_number || "Senza fattura";
      }

      if (!map.has(key)) {
        map.set(key, {
          key,
          label,
          rows: [],
        });
      }

      map.get(key).rows.push(r);
    }

    return Array.from(map.values()).map((g) => ({
      ...g,
      totals: computeTotals(g.rows),
    }));
  }, [sortedRows, groupMode]);

  function applyPeriod(range) {
    if (range.from && range.to && range.from > range.to) return;
    if (filters.from === range.from && filters.to === range.to) return;
    setPage(1);
    setFilters(prev => ({ ...prev, ...range }));
  }

  function clearPeriod() {
    if (!filters.from && !filters.to) return;
    setPage(1);
    setFilters(prev => ({ ...prev, from: "", to: "" }));
  }

  function handleChangeFornitore(e) {
    const val = e.target.value || "";
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      fornitore_id: val,
    }));
  }

  function handleChangeContab(e) {
    const val = e.target.value || "all";
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      contab_status: val,
    }));
  }

  function handleChangeGroupMode(e) {
    setGroupMode(e.target.value || "none");
  }

  function handlePrevPage() {
    setPage((p) => Math.max(1, p - 1));
  }

  function handleNextPage() {
    setPage((p) => Math.min(totalPages, p + 1));
  }

  function handleSort(key) {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  }

  function getSortArrow(key) {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  }

  return (
    <div className="acc-ord-page">
      <div className="acc-ord-inner">
        <header className="acc-ord-header">
          <div>
            <h1>Contabilità · Ordini</h1>
            {totals.articoli_cents == null && <p>«—» indica componenti storiche non archiviate: il relativo totale non viene ricostruito dai dati correnti. Le quote fatturate restano quelle originali.</p>}
          </div>
        </header>

        <section className="acc-ord-filters">
          <div className="acc-ord-filter-group acc-ord-filter-group-period">
            <label className="acc-ord-filter-label">Periodo</label>

            <div className="acc-ord-filter-row acc-ord-filter-row-period">
              <OrderPeriodPicker from={filters.from} to={filters.to} onApply={applyPeriod} />
              <button type="button" className="acc-ord-btn acc-ord-btn-ghost acc-ord-btn-date-action" onClick={clearPeriod}>Pulisci</button>
            </div>

            {hasAppliedDateFilters && (
              <div className="acc-ord-filter-hint">
                Filtri attivi:
                {filters.from && (
                  <span>
                    {" "}
                    dal <strong>{isoToDisplay(filters.from)}</strong>
                  </span>
                )}
                {filters.to && (
                  <span>
                    {" "}
                    al <strong>{isoToDisplay(filters.to)}</strong>
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="acc-ord-filter-group">
            <label className="acc-ord-filter-label">Fornitore</label>
            <select
              className="acc-ord-input acc-ord-select"
              value={filters.fornitore_id || ""}
              onChange={handleChangeFornitore}
              disabled={loadingSuppliers}
            >
              <option value="">Tutti i fornitori</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div className="acc-ord-filter-group">
            <label className="acc-ord-filter-label">Stato contabile</label>
            <select
              className="acc-ord-input acc-ord-select"
              value={filters.contab_status}
              onChange={handleChangeContab}
            >
              {CONTAB_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="acc-ord-filter-group">
            <label className="acc-ord-filter-label">Raggruppa per</label>
            <select
              className="acc-ord-input acc-ord-select"
              value={groupMode}
              onChange={handleChangeGroupMode}
            >
              {GROUP_MODES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section className="acc-ord-table-section">
          <div className="acc-ord-table-wrap">
            <table className="acc-ord-table">
              <thead>
                <tr>
                  <th className="acc-ord-col-date">
                    <button
                      type="button"
                      className={`acc-ord-th-btn ${
                        sortConfig.key === "created_at" ? "is-active" : ""
                      }`}
                      onClick={() => handleSort("created_at")}
                    >
                      <span>Data</span>
                      <span className="acc-ord-th-arrow">
                        {getSortArrow("created_at")}
                      </span>
                    </button>
                  </th>

                  <th className="acc-ord-col-id">
                    <button
                      type="button"
                      className={`acc-ord-th-btn ${
                        sortConfig.key === "id" ? "is-active" : ""
                      }`}
                      onClick={() => handleSort("id")}
                    >
                      <span>Ord</span>
                      <span className="acc-ord-th-arrow">
                        {getSortArrow("id")}
                      </span>
                    </button>
                  </th>

                  <th className="acc-ord-col-ft">
                    <button
                      type="button"
                      className={`acc-ord-th-btn ${
                        sortConfig.key === "invoice_number" ? "is-active" : ""
                      }`}
                      onClick={() => handleSort("invoice_number")}
                    >
                      <span>FT</span>
                      <span className="acc-ord-th-arrow">
                        {getSortArrow("invoice_number")}
                      </span>
                    </button>
                  </th>

                  <th className="acc-ord-col-cliente">
                    <button
                      type="button"
                      className={`acc-ord-th-btn ${
                        sortConfig.key === "cliente" ? "is-active" : ""
                      }`}
                      onClick={() => handleSort("cliente")}
                    >
                      <span>Cliente</span>
                      <span className="acc-ord-th-arrow">
                        {getSortArrow("cliente")}
                      </span>
                    </button>
                  </th>

                  <th className="acc-ord-col-fornitore">
                    <button
                      type="button"
                      className={`acc-ord-th-btn ${
                        sortConfig.key === "fornitore" ? "is-active" : ""
                      }`}
                      onClick={() => handleSort("fornitore")}
                    >
                      <span>Fornitore</span>
                      <span className="acc-ord-th-arrow">
                        {getSortArrow("fornitore")}
                      </span>
                    </button>
                  </th>

                  <th className="acc-ord-col-city">
                    <button
                      type="button"
                      className={`acc-ord-th-btn ${
                        sortConfig.key === "citta" ? "is-active" : ""
                      }`}
                      onClick={() => handleSort("citta")}
                    >
                      <span>Città</span>
                      <span className="acc-ord-th-arrow">
                        {getSortArrow("citta")}
                      </span>
                    </button>
                  </th>

                  <th className="acc-ord-col-status">
                    <button
                      type="button"
                      className={`acc-ord-th-btn ${
                        sortConfig.key === "contab_status" ? "is-active" : ""
                      }`}
                      onClick={() => handleSort("contab_status")}
                    >
                      <span>Contabilità</span>
                      <span className="acc-ord-th-arrow">
                        {getSortArrow("contab_status")}
                      </span>
                    </button>
                  </th>

                  <th className="acc-ord-col-num">
                    <button
                      type="button"
                      className={`acc-ord-th-btn ${
                        sortConfig.key === "articoli_cents" ? "is-active" : ""
                      }`}
                      onClick={() => handleSort("articoli_cents")}
                    >
                      <span>Art €</span>
                      <span className="acc-ord-th-arrow">
                        {getSortArrow("articoli_cents")}
                      </span>
                    </button>
                  </th>

                  <th className="acc-ord-col-num acc-ord-col-num--tight">
                    <button
                      type="button"
                      className={`acc-ord-th-btn ${
                        sortConfig.key === "promo_cents" ? "is-active" : ""
                      }`}
                      onClick={() => handleSort("promo_cents")}
                    >
                      <span>Promo €</span>
                      <span className="acc-ord-th-arrow">
                        {getSortArrow("promo_cents")}
                      </span>
                    </button>
                  </th>

                  <th className="acc-ord-col-num acc-ord-col-num--tight">
                    <button
                      type="button"
                      className={`acc-ord-th-btn ${
                        sortConfig.key === "articoli_netti_cents"
                          ? "is-active"
                          : ""
                      }`}
                      onClick={() => handleSort("articoli_netti_cents")}
                    >
                      <span>Art netti €</span>
                      <span className="acc-ord-th-arrow">
                        {getSortArrow("articoli_netti_cents")}
                      </span>
                    </button>
                  </th>

                  <th className="acc-ord-col-num">
                    <button
                      type="button"
                      className={`acc-ord-th-btn ${
                        sortConfig.key === "ns_articoli_cents"
                          ? "is-active"
                          : ""
                      }`}
                      onClick={() => handleSort("ns_articoli_cents")}
                    >
                      <span>Ns Art €</span>
                      <span className="acc-ord-th-arrow">
                        {getSortArrow("ns_articoli_cents")}
                      </span>
                    </button>
                  </th>

                  <th className="acc-ord-col-num">
                    <button
                      type="button"
                      className={`acc-ord-th-btn ${
                        sortConfig.key === "rosa_cents" ? "is-active" : ""
                      }`}
                      onClick={() => handleSort("rosa_cents")}
                    >
                      <span>Rosa €</span>
                      <span className="acc-ord-th-arrow">
                        {getSortArrow("rosa_cents")}
                      </span>
                    </button>
                  </th>

                  <th className="acc-ord-col-num">
                    <button
                      type="button"
                      className={`acc-ord-th-btn ${
                        sortConfig.key === "ns_rosa_cents" ? "is-active" : ""
                      }`}
                      onClick={() => handleSort("ns_rosa_cents")}
                    >
                      <span>Ns Rosa €</span>
                      <span className="acc-ord-th-arrow">
                        {getSortArrow("ns_rosa_cents")}
                      </span>
                    </button>
                  </th>

                  <th className="acc-ord-col-num">
                    <button
                      type="button"
                      className={`acc-ord-th-btn ${
                        sortConfig.key === "flat_fee_cents" ? "is-active" : ""
                      }`}
                      onClick={() => handleSort("flat_fee_cents")}
                    >
                      <span>Fisso</span>
                      <span className="acc-ord-th-arrow">
                        {getSortArrow("flat_fee_cents")}
                      </span>
                    </button>
                  </th>

                  <th className="acc-ord-col-num acc-ord-col-num-strong">
                    <button
                      type="button"
                      className={`acc-ord-th-btn ${
                        sortConfig.key === "ns_total_cents" ? "is-active" : ""
                      }`}
                      onClick={() => handleSort("ns_total_cents")}
                    >
                      <span>Ns Tot €</span>
                      <span className="acc-ord-th-arrow">
                        {getSortArrow("ns_total_cents")}
                      </span>
                    </button>
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr className="acc-ord-row-skel">
                    <td colSpan={15}>Caricamento ordini…</td>
                  </tr>
                )}

                {!loading && groupedData.length === 0 && (
                  <tr>
                    <td colSpan={15} className="acc-ord-empty">
                      Nessun ordine trovato con i filtri attuali.
                    </td>
                  </tr>
                )}

                {!loading &&
                  groupedData.map((group) => (
                    <React.Fragment key={group.key}>
                      {groupMode !== "none" && (
                        <tr className="acc-ord-group-row">
                          <td colSpan={15}>
                            <div className="acc-ord-group-label">
                              <span className="acc-ord-group-label-main">
                                {groupMode === "fornitore"
                                  ? "Fornitore"
                                  : groupMode === "citta"
                                  ? "Città"
                                  : "Fattura"}
                                : <strong>{group.label}</strong>
                              </span>
                              <span className="acc-ord-group-label-meta">
                                {group.rows.length} ordini ·{" "}
                                {fmtEur(group.totals.ns_total_cents)} nostro
                              </span>
                            </div>
                          </td>
                        </tr>
                      )}

                      {group.rows.map((r) => (
                        <tr key={r.id}>
                          <td className="acc-ord-col-date">
                            <span className="acc-ord-date-main">
                              {fmtDate(r.created_date || r.created_at)}
                            </span>
                          </td>

                          <td className="acc-ord-col-id">
                            <button
                              type="button"
                              className="acc-ord-link-id"
                              onClick={() => openOrderDetail(r.id)}
                              title={`Apri ordine ${r.id}`}
                            >
                              <span className="acc-ord-eye">👁</span>
                              <span className="acc-ord-mono">{r.id}</span>
                            </button>
                          </td>

                          <td
                            className="acc-ord-col-ft"
                            title={r.invoice_number || ""}
                          >
                            {r.invoice_number || "—"}
                          </td>

                          <td className="acc-ord-col-cliente">
                            <button
                              type="button"
                              className="acc-ord-link-client"
                              onClick={() => openClientDetail(r.cliente_id)}
                              title={`Apri cliente ${r.cliente || ""}`}
                              disabled={!r.cliente_id}
                            >
                              {r.cliente || "—"}
                            </button>
                          </td>

                          <td className="acc-ord-col-fornitore">
                            <button
                              type="button"
                              className="acc-ord-link"
                              onClick={() => goSupplier(r.fornitore_id)}
                              title={`Apri gestione servizi: ${
                                r.fornitore || ""
                              }`}
                            >
                              {r.fornitore || "—"}
                            </button>
                          </td>

                          <td className="acc-ord-col-city">
                            {r.citta || ""}
                            {r.provincia ? ` (${r.provincia})` : ""}
                          </td>

                          <td className="acc-ord-col-status">
                            {renderContabStatus(r.contab_status)}
                          </td>

                          <td className="acc-ord-col-num">
                            {fmtEur(r.articoli_cents)}
                          </td>
                          <td className="acc-ord-col-num acc-ord-col-num--promo">
                            {Number(r.promo_cents) > 0
                              ? `−${fmtEur(r.promo_cents)}`
                              : "—"}
                          </td>
                          <td className="acc-ord-col-num">
                            {fmtEur(r.articoli_netti_cents)}
                          </td>
                          <td className="acc-ord-col-num">
                            {fmtEur(r.ns_articoli_cents)}
                          </td>
                          <td className="acc-ord-col-num">
                            {fmtEur(r.rosa_cents)}
                          </td>
                          <td className="acc-ord-col-num">
                            {fmtEur(r.ns_rosa_cents)}
                          </td>
                          <td className="acc-ord-col-num">
                            {fmtEur(r.flat_fee_cents)}
                          </td>
                          <td className="acc-ord-col-num acc-ord-col-strong acc-ord-col-num-strong">
                            {fmtEur(r.ns_total_cents)}
                          </td>
                        </tr>
                      ))}

                      {groupMode !== "none" && (
                        <tr className="acc-ord-group-total-row">
                          <td className="acc-ord-col-date" />
                          <td className="acc-ord-group-total-label" colSpan={6}>
                            Totale gruppo{" "}
                            {groupMode === "fornitore"
                              ? "fornitore"
                              : groupMode === "citta"
                              ? "città"
                              : "fattura"}
                            : <strong>{group.label}</strong>
                          </td>
                          <td className="acc-ord-col-num">
                            {fmtEur(group.totals.articoli_cents)}
                          </td>
                          <td className="acc-ord-col-num acc-ord-col-num--promo">
                            {Number(group.totals.promo_cents) > 0
                              ? `−${fmtEur(group.totals.promo_cents)}`
                              : "—"}
                          </td>
                          <td className="acc-ord-col-num">
                            {fmtEur(group.totals.articoli_netti_cents)}
                          </td>
                          <td className="acc-ord-col-num">
                            {fmtEur(group.totals.ns_articoli_cents)}
                          </td>
                          <td className="acc-ord-col-num">
                            {fmtEur(group.totals.rosa_cents)}
                          </td>
                          <td className="acc-ord-col-num">
                            {fmtEur(group.totals.ns_rosa_cents)}
                          </td>
                          <td className="acc-ord-col-num">
                            {fmtEur(group.totals.flat_fee_cents)}
                          </td>
                          <td className="acc-ord-col-num acc-ord-col-strong acc-ord-col-num-strong">
                            {fmtEur(group.totals.ns_total_cents)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="acc-ord-totals">
            <div className="acc-ord-totals-header">
              <span className="acc-ord-totals-title">
                Totali nel risultato corrente
              </span>
              <span className="acc-ord-totals-subtitle">
                ({fmtEur(totals.ns_total_cents)} totale nostro)
              </span>
            </div>

            <div className="acc-ord-totals-grid">
              <div className="acc-ord-total-item">
                <span className="acc-ord-total-label">Articoli</span>
                <span className="acc-ord-total-value">
                  {fmtEur(totals.articoli_cents)}
                </span>
              </div>

              <div className="acc-ord-total-item">
                <span className="acc-ord-total-label">Promo</span>
                <span className="acc-ord-total-value">
                  {Number(totals.promo_cents) > 0
                    ? `−${fmtEur(totals.promo_cents)}`
                    : "—"}
                </span>
              </div>

              <div className="acc-ord-total-item">
                <span className="acc-ord-total-label">Articoli netti</span>
                <span className="acc-ord-total-value">
                  {fmtEur(totals.articoli_netti_cents)}
                </span>
              </div>

              <div className="acc-ord-total-item">
                <span className="acc-ord-total-label">Nostro su articoli</span>
                <span className="acc-ord-total-value">
                  {fmtEur(totals.ns_articoli_cents)}
                </span>
              </div>

              <div className="acc-ord-total-item">
                <span className="acc-ord-total-label">Rosa</span>
                <span className="acc-ord-total-value">
                  {fmtEur(totals.rosa_cents)}
                </span>
              </div>

              <div className="acc-ord-total-item">
                <span className="acc-ord-total-label">Nostro su rosa</span>
                <span className="acc-ord-total-value">
                  {fmtEur(totals.ns_rosa_cents)}
                </span>
              </div>

              <div className="acc-ord-total-item">
                <span className="acc-ord-total-label">Fisso</span>
                <span className="acc-ord-total-value">
                  {fmtEur(totals.flat_fee_cents)}
                </span>
              </div>

              <div className="acc-ord-total-item acc-ord-total-strong">
                <span className="acc-ord-total-label">Totale nostro</span>
                <span className="acc-ord-total-value">
                  {fmtEur(totals.ns_total_cents)}
                </span>
              </div>
            </div>
          </div>

          <div className="acc-ord-pagination">
            <button
              type="button"
              className="acc-ord-btn acc-ord-btn-ghost"
              onClick={handlePrevPage}
              disabled={page <= 1 || loading}
            >
              ← Prec.
            </button>

            <span className="acc-ord-page-info">
              Pagina {page} / {totalPages}
            </span>

            <button
              type="button"
              className="acc-ord-btn acc-ord-btn-ghost"
              onClick={handleNextPage}
              disabled={page >= totalPages || loading}
            >
              Succ. →
            </button>
          </div>
        </section>
      </div>

      {openClientId && (
        <div className="ac-overlay" role="dialog" aria-modal="true">
          <div className="ac-modal">
            <div className="ac-modal-h">
              <div className="ac-title">Cliente #{openClientId}</div>
              <button className="ac-close" onClick={closeClientDetail}>
                ✕
              </button>
            </div>

            <div className="ac-modal-body">
              <div className="ac-form">
                <div className="ac-profile-embed">
                  <Profilo embedded targetUserId={openClientId} />
                </div>
              </div>
            </div>

            <div className="ac-modal-f">
              <button className="ac-btn" onClick={closeClientDetail}>
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      <OrderDetailModal
        open={detailOpen}
        orderId={detailId}
        onClose={closeOrderDetail}
      />
    </div>
  );
}
